"use client";

import { useEffect } from "react";
import { create } from "zustand";

import statesConfig, {
  isWalledStateConfig,
  type StateConfig,
} from "@/lib/states-config";

const LAST_STATE_STORAGE_KEY = "lastViewedState";

export interface LastViewedStateStore {
  stateConfig: StateConfig | null;
  setStateConfig: (stateConfig: StateConfig) => void;
}

const getInitialState = (): StateConfig | null => {
  if (typeof localStorage === "undefined") return null;

  const storedKey = localStorage.getItem(LAST_STATE_STORAGE_KEY);
  if (storedKey) {
    const stateConfig = (Object.values(statesConfig) as StateConfig[]).find(
      (config) => config.route === storedKey,
    );
    if (!stateConfig || isWalledStateConfig(stateConfig)) {
      return null;
    }
    return stateConfig;
  }

  return null;
};

const useLastViewedState = create<LastViewedStateStore>()((set) => ({
  stateConfig: getInitialState(),
  setStateConfig: (stateConfig: StateConfig) => set({ stateConfig }),
}));

useLastViewedState.subscribe((state) => {
  if (state.stateConfig) {
    localStorage?.setItem(LAST_STATE_STORAGE_KEY, state.stateConfig.route);
  }
});

export default useLastViewedState;

export function LastViewedStateTracker({
  stateConfig,
}: {
  stateConfig: StateConfig;
}) {
  const { setStateConfig, stateConfig: lastStateConfig } = useLastViewedState();

  useEffect(() => {
    if (isWalledStateConfig(stateConfig)) return;

    if (stateConfig?.route !== lastStateConfig?.route) {
      setStateConfig(stateConfig);
    }
  }, [lastStateConfig, stateConfig, setStateConfig]);

  return null;
}
